import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Child, ChildDaycare, ChildSection } from '@/types/Child'
import { Parent } from '@/types/Parent'
import { Section } from '@/types/Section'
import { Daycare } from '@/types/Daycare'
import { ChildChecked, SectionAndChild } from './types'

interface UseSectionChildSelectionProps {
    daycare?: Daycare
    sections?: Section[]
    parents?: Parent[]
    postChilds: number[]
    setPostChilds: Dispatch<SetStateAction<number[]>>
    checkAllByDefault?: boolean
}

export interface UseSectionChildSelectionReturn {
    sectionAndChilds: SectionAndChild[]
    childsWithoutSection: ChildChecked[]
    handleSectionChange: (sectionIndex: number) => void
    handleChildChange: (sectionId: number, childId: number) => void
    handleWithoutSectionChange: () => void
    handleChildWithoutSectionChange: (childId: number) => void
}

export function useSectionChildSelection({
    daycare,
    sections,
    parents,
    postChilds,
    setPostChilds,
    checkAllByDefault = true
}: UseSectionChildSelectionProps): UseSectionChildSelectionReturn {
    const [sectionAndChilds, setSectionAndChilds] = useState<SectionAndChild[]>([])
    const [childsWithoutSection, setChildsWithoutSection] = useState<ChildChecked[]>([])

    const createChildChecked: (child: Child, defaultCheck?: boolean) => ChildChecked = useCallback(
        (child: Child, defaultCheck: boolean = true): ChildChecked => ({
            child,
            isCheck: postChilds.length > 0 ? postChilds.includes(child.id) : checkAllByDefault && defaultCheck
        }),
        [postChilds, checkAllByDefault]
    )

    useEffect(() => {
        if (!daycare || !parents || !sections) return

        const newSectionAndChilds: SectionAndChild[] = sections
            .map((section: Section): SectionAndChild => {
                const childs: ChildChecked[] = uniqueChilds(
                    parents.flatMap((parent: Parent): ChildChecked[] =>
                        parent.childs
                            .filter((child: Child): boolean => {
                                const childDaycare: ChildDaycare | undefined = child.daycares.find(
                                    (cd: ChildDaycare): boolean =>
                                        cd.daycareId === daycare.id &&
                                        cd.visible &&
                                        cd.sections.length > 0 &&
                                        cd.sections.some(
                                            (s: ChildSection): boolean => s.sectionId === section.id && s.isPrimary
                                        )
                                )
                                return childDaycare !== undefined
                            })
                            .sort((a: Child, b: Child): number => a.firstname.localeCompare(b.firstname))
                            .map((child: Child): ChildChecked => createChildChecked(child, true))
                    )
                )
                return { section, childs }
            })
            .filter((sac: SectionAndChild): boolean => sac.childs.length > 0)

        setSectionAndChilds(newSectionAndChilds)

        const newChildsWithoutSection: ChildChecked[] = uniqueChilds(
            parents.flatMap((parent: Parent): ChildChecked[] =>
                parent.childs
                    .filter((child: Child): boolean => {
                        const childDaycare: ChildDaycare | undefined = child.daycares.find(
                            (cd: ChildDaycare): boolean =>
                                cd.daycareId === daycare.id && cd.visible && cd.sections.length === 0
                        )
                        return childDaycare !== undefined
                    })
                    .sort((a: Child, b: Child): number => a.firstname.localeCompare(b.firstname))
                    .map((child: Child): ChildChecked => createChildChecked(child, false))
            )
        )

        setChildsWithoutSection(newChildsWithoutSection)

        const allSelectedChildIds: number[] = [
            ...newSectionAndChilds.flatMap((sac: SectionAndChild): number[] =>
                sac.childs.filter((c: ChildChecked): boolean => c.isCheck).map((c: ChildChecked): number => c.child.id)
            ),
            ...newChildsWithoutSection
                .filter((c: ChildChecked): boolean => c.isCheck)
                .map((c: ChildChecked): number => c.child.id)
        ]
        setPostChilds(allSelectedChildIds)
    }, [daycare, parents, sections])

    const handleSectionChange: (sectionIndex: number) => void = useCallback(
        (sectionIndex: number): void => {
            const section: SectionAndChild = sectionAndChilds[sectionIndex]
            const allChecked: boolean = section.childs.every((child: ChildChecked): boolean => child.isCheck)
            const childIds: number[] = section.childs.map((child: ChildChecked): number => child.child.id)

            setSectionAndChilds((prev: SectionAndChild[]): SectionAndChild[] => {
                const newSections: SectionAndChild[] = [...prev]
                newSections[sectionIndex] = {
                    ...newSections[sectionIndex],
                    childs: newSections[sectionIndex].childs.map(
                        (child: ChildChecked): ChildChecked => ({
                            ...child,
                            isCheck: !allChecked
                        })
                    )
                }
                return newSections
            })

            setPostChilds((prev: number[]): number[] => {
                if (!allChecked) {
                    return [...new Set([...prev, ...childIds])]
                } else {
                    return prev.filter((id: number): boolean => !childIds.includes(id))
                }
            })
        },
        [sectionAndChilds, setPostChilds]
    )

    const handleChildChange: (sectionId: number, childId: number) => void = useCallback(
        (sectionId: number, childId: number): void => {
            const sectionIndex: number = sectionAndChilds.findIndex(
                (sac: SectionAndChild): boolean => sac.section.id === sectionId
            )
            if (sectionIndex === -1) return

            const childIndex: number = sectionAndChilds[sectionIndex].childs.findIndex(
                (c: ChildChecked): boolean => c.child.id === childId
            )
            if (childIndex === -1) return

            const isCurrentlyChecked: boolean = sectionAndChilds[sectionIndex].childs[childIndex].isCheck

            setSectionAndChilds((prev: SectionAndChild[]): SectionAndChild[] => {
                const newSections: SectionAndChild[] = [...prev]
                newSections[sectionIndex] = {
                    ...newSections[sectionIndex],
                    childs: newSections[sectionIndex].childs.map(
                        (c: ChildChecked, idx: number): ChildChecked =>
                            idx === childIndex ? { ...c, isCheck: !c.isCheck } : c
                    )
                }
                return newSections
            })

            setPostChilds((prev: number[]): number[] => {
                if (isCurrentlyChecked) {
                    return prev.filter((id: number): boolean => id !== childId)
                } else {
                    return [...prev, childId]
                }
            })
        },
        [sectionAndChilds, setPostChilds]
    )

    const handleWithoutSectionChange: () => void = useCallback((): void => {
        const allChecked: boolean = childsWithoutSection.every((child: ChildChecked): boolean => child.isCheck)
        const childIds: number[] = childsWithoutSection.map((child: ChildChecked): number => child.child.id)

        setChildsWithoutSection((prev: ChildChecked[]): ChildChecked[] =>
            prev.map((child: ChildChecked): ChildChecked => ({ ...child, isCheck: !allChecked }))
        )

        setPostChilds((prev: number[]): number[] => {
            if (!allChecked) {
                return [...new Set([...prev, ...childIds])]
            } else {
                return prev.filter((id: number): boolean => !childIds.includes(id))
            }
        })
    }, [childsWithoutSection, setPostChilds])

    const handleChildWithoutSectionChange: (childId: number) => void = useCallback(
        (childId: number): void => {
            const childIndex: number = childsWithoutSection.findIndex(
                (c: ChildChecked): boolean => c.child.id === childId
            )
            if (childIndex === -1) return

            const isCurrentlyChecked: boolean = childsWithoutSection[childIndex].isCheck

            setChildsWithoutSection((prev: ChildChecked[]): ChildChecked[] =>
                prev.map(
                    (c: ChildChecked, idx: number): ChildChecked =>
                        idx === childIndex ? { ...c, isCheck: !c.isCheck } : c
                )
            )

            setPostChilds((prev: number[]): number[] => {
                if (isCurrentlyChecked) {
                    return prev.filter((id: number): boolean => id !== childId)
                } else {
                    return [...prev, childId]
                }
            })
        },
        [childsWithoutSection, setPostChilds]
    )

    return {
        sectionAndChilds,
        childsWithoutSection,
        handleSectionChange,
        handleChildChange,
        handleWithoutSectionChange,
        handleChildWithoutSectionChange
    }
}

function uniqueChilds(childs: ChildChecked[]): ChildChecked[] {
    return Array.from(new Map(childs.map((c: ChildChecked): [number, ChildChecked] => [c.child.id, c])).values())
}
